import { makeArticle, saintRelated } from "./learnFactory";

export const pageSaintWorkDuringTreatment = makeArticle({
  slug: "can-i-work-during-saint-tms-treatment-week",
  title: "Can I Work During SAINT® Treatment Week? | Neuro Wellness",
  description:
    "Can you work during SAINT® treatment week? Most patients cannot work full-time but can work remotely during 50-minute breaks. Learn what to expect.",
  canonical: "/learn/can-i-work-during-saint-tms-treatment-week",
  h1: "Can I Work During SAINT® Treatment Week?",
  breadcrumbLabel: "Working During SAINT® Week",
  shortAnswer:
    "Most patients cannot work full-time during the SAINT® treatment week because they spend most of each day at the clinic. However, many patients work remotely on a laptop during the 50-minute breaks between sessions. Each day includes 10 sessions of about 10 minutes each, with roughly 50-minute breaks between them. The treatment week is 5 consecutive days, after which patients return to normal activities. Planning ahead for time off or remote work is recommended.",
  keyTakeaways: [
    "Most patients cannot work full-time during the 5-day treatment week.",
    "Remote work during 50-minute breaks is common and feasible.",
    "Each day includes 10 sessions with breaks between them.",
    "The treatment week is 5 consecutive days.",
    "Planning ahead for time off or remote work is recommended.",
  ],
  sections: [
    {
      heading: "The Treatment Day Schedule",
      paragraphs: [
        "Each SAINT® treatment day includes 10 sessions of about 10 minutes each, separated by roughly 50-minute breaks. With this schedule, patients spend most of each day at the clinic. The breaks are a deliberate part of the protocol — they allow the brain to respond to the stimulation pattern.",
        "Because the treatment day runs most of the day, full-time in-person work during the treatment week is generally not practical. However, the 50-minute breaks provide substantial time for remote work, reading, or rest.",
      ],
    },
    {
      heading: "Working Remotely During Breaks",
      paragraphs: [
        "Many patients work remotely on a laptop during the 50-minute breaks between sessions. The breaks are long enough to handle emails, join calls, or do focused work. Neuro Wellness provides private treatment suites where patients can work comfortably and privately between sessions.",
        "If you plan to work during treatment, bring a laptop, headphones, and anything else you need. Let your treatment team know your schedule so they can plan sessions accordingly.",
      ],
    },
    {
      heading: "Planning for the Treatment Week",
      paragraphs: [
        "If you are employed, plan ahead for the treatment week. Options include:",
      ],
      list: [
        "Taking a week of leave or vacation time.",
        "Arranging remote work for the week.",
        "Reducing hours or shifting schedule to accommodate treatment days.",
        "Discussing accommodations with your employer.",
      ],
      paragraphsAfter: [
        "The 5-day schedule is one of the reasons some patients choose SAINT® over conventional TMS — instead of taking 6 weeks of daily visits, the entire course is completed in one week.",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "While most patients cannot work full-time in person during the treatment week, the 50-minute breaks make remote work feasible. The treatment week is 5 days, after which patients return to normal activities.",
        "If you are concerned about taking time off work, discuss your schedule with the Neuro Wellness team during your consultation. They can help you plan the treatment week around your work commitments.",
      ],
      links: [
        {
          to: "/learn/what-to-expect-during-saint-tms",
          text: "See a day-by-day guide to the 5 days of SAINT®",
        },
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Can I work during SAINT® treatment week?",
      answer:
        "Most patients cannot work full-time in person during the treatment week because they spend most of each day at the clinic. However, many patients work remotely on a laptop during the 50-minute breaks between sessions. The treatment week is 5 consecutive days, after which patients return to normal activities.",
    },
    {
      question: "How long is each SAINT® treatment day?",
      answer:
        "Each day includes 10 sessions of about 10 minutes each, with roughly 50-minute breaks between them. Patients should expect to spend most of each day at the clinic. The breaks provide time for rest, reading, or remote work.",
    },
    {
      question: "Can I work remotely between SAINT® sessions?",
      answer:
        "Yes. The 50-minute breaks between sessions are long enough to handle emails, join calls, or do focused work. Neuro Wellness provides private treatment suites where patients can work comfortably between sessions. Bring a laptop and headphones if you plan to work.",
    },
    {
      question: "Do I need to take time off work for SAINT®?",
      answer:
        "You will likely need to take at least a week off from in-person work. The treatment week is 5 consecutive days, and you will spend most of each day at the clinic. Remote work during breaks is feasible. Plan ahead by arranging leave, remote work, or schedule adjustments.",
    },
    {
      question: "Is the 5-day schedule an advantage over conventional TMS?",
      answer:
        "For many patients, yes. Conventional TMS requires daily visits for 4–6 weeks, which is difficult for people who cannot take extended time off. SAINT® compresses the entire course into 5 days, which may be more practical for some patients despite requiring a full week at the clinic.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  relatedLinks: saintRelated,
});
